/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';

import { IRightRailExpanderConfig } from 'ng/modules/right-rail/components';

import {
    Observable,
    Subject,
    Subscription,
} from 'rxjs';

import { ConnectedPosition } from '@angular/cdk/overlay';

import {
    SignpostPosition,
    signpostPositionMap,
    SIGNPOST_BOTTOM_LEFT_CONNECTED_POSITION,
    SIGNPOST_LEFT_CONNECTED_POSITION,
    SIGNPOST_TOP_LEFT_CONNECTED_POSITION,
} from 'ng/modules/tooltip/components/signpost/signpost.constants';

import { map, take } from 'rxjs/operators';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';
import { LogsApiRequestService } from 'ng/modules/logs/services/logs-api-request.service';
import * as l10n from './traffic-logs-sidebar.l10n';
import './traffic-logs-sidebar.component.less';
import { TrafficLogsSignpostStore } from './traffic-logs-signpost.store';

import {
    ITrafficLogsStates,
    IVsConfigForTrafficLogs,
    TrafficLogsStore,
} from '../traffic-logs.store';

export type TStateFilters = string[];

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

/**
 * @description
 *
 *     Component for setting the side bar tree view.
 *
 * @author Zhiqian Liu, Alex Klyuev, Suraj Kumar, Yogesh Patil
 */
@Component({
    selector: 'traffic-logs-sidebar',
    templateUrl: './traffic-logs-sidebar.component.html',
})
export class TrafficLogsSidebarComponent implements OnInit, OnDestroy {
    /**
     * The ID of the pool used to filter logs from a VS.
     */
    @Input()
    public poolId?: string;

    /**
     * Config for generating the side bar expander list view.
     */
    public expanderListConfig$: Observable<IRightRailExpanderConfig[]>;

    /**
     * Signpost position priorities.
     */
    public signpostPositionPriorities: ConnectedPosition[] = [
        SIGNPOST_LEFT_CONNECTED_POSITION,
        SIGNPOST_TOP_LEFT_CONNECTED_POSITION,
        SIGNPOST_BOTTOM_LEFT_CONNECTED_POSITION,
    ];

    /**
     * Decide signpost position relative to its parent.
     */
    public signpostPosition: SignpostPosition;

    /**
     * Keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Subscription to close the signpost.
     */
    public closeSignpostSubscription: Subscription;

    /**
     * Subject to control the hide/show signpost close button.
     */
    public readonly signpostCloseButtonControl$ = new Subject<boolean>();

    /**
     * Default visibility state for expander items. If an item in `expanderListConfig`
     * does not have its own `hidden` property, this value is used as a fallback in the template.
     */
    public readonly hidden = false;

    constructor(
        public readonly trafficLogsSignpostStore: TrafficLogsSignpostStore,
        logsApiRequestService: LogsApiRequestService,
        public readonly trafficLogsStore: TrafficLogsStore<ITrafficLogsStates>,
        private l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.closeSignpostSubscription = trafficLogsSignpostStore.closeSignpost$.subscribe(() => {
            // Cancel all the ongoing signpost requests before making new requests.
            trafficLogsSignpostStore.selectedSignpostRequestIds$.pipe(take(1)).subscribe(
                requestIds => requestIds?.forEach(
                    requestId => logsApiRequestService.cancelRequest(requestId),
                ),
            );

            this.closeSignpost();
        });
    }

    /** @override */
    public ngOnInit(): void {
        this.expanderListConfig$ = this.trafficLogsStore.vsConfig$.pipe(
            map(vsConfig => {
                if (vsConfig.isDNS) {
                    return this.getDnsExpanderListViewConfig(vsConfig);
                }

                return this.getNonDnsExpanderListViewConfig(vsConfig);
            }),
        );
    }

    /**
     * Callback to ngFor-track by.
     */
    public trackByIndex(index: number): number {
        return index;
    }

    /** @override */
    public ngOnDestroy(): void {
        this.signpostCloseButtonControl$.complete();
        this.closeSignpostSubscription.unsubscribe();
    }

    /**
     * Notify signpost to update postion when the connected position from avi-tooltip changes
     */
    public handleSignpostConnectedPositionChange(
        signpostConnectedPosition: ConnectedPosition,
    ): void {
        this.signpostPosition = signpostPositionMap.get(signpostConnectedPosition);
    }

    /**
     * Close the signpost.
     */
    public closeSignpost(): void {
        this.signpostCloseButtonControl$.next(false);
    }

    /**
     * Set default state when clicked outside signpost.
     */
    public handleSignpostClosed(isOpen: boolean): void {
        if (!isOpen) {
            this.trafficLogsSignpostStore.setDefaultState();
        }
    }

    /**
     * Add saved filters to the search bar.
     */
    public addSavedSearchFilters(queryStrings: TStateFilters): void {
        this.trafficLogsStore.replaceFilterQueryStrings(queryStrings);
    }

    /**
     * Remove the given saved search from the store.
     */
    public removeSavedSearch(e: MouseEvent, name: string): void {
        // stop click events in parent components from running
        e.stopPropagation();
        this.trafficLogsStore.removeSavedSearch(name);
    }

    /**
     * Set the config for the expander list view of DNS VS based on headers and children constants.
     */
    private getDnsExpanderListViewConfig(vs: IVsConfigForTrafficLogs): IRightRailExpanderConfig[] {
        return [
            {
                icon: 'user',
                headerText: this.l10nService.getMessage(l10nKeys.clientAnalyticsLabel),
                children: [
                    {
                        displayText: this.l10nService.getMessage(
                            globalL10nKeys.clientIpAddressLabel,
                        ),
                        configKey: 'ip-addr',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.locationLabel),
                        configKey: 'country',
                    },
                ],
            },
            {
                icon: 'share',
                headerText: this.l10nService.getMessage(l10nKeys.loadBalancerAnalyticsLabel),
                children: [
                    {
                        displayText: this.l10nService.getMessage(globalL10nKeys.serviceEngineLabel),
                        configKey: 'service_engine',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.serverIpAddressLabel),
                        configKey: 'server-ip-addr',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.vsIpAddressLabel),
                        configKey: 'vs_ip',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.recordSourceLabel),
                        configKey: 'dns_etype',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.gslbServiceNameLabel),
                        configKey: 'gslbservice_name',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.fqdnDomainNameLabel),
                        configKey: 'dns_fqdn',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.tcpEndToEndLabel),
                        configKey: 'e2e-timing',
                        hidden: !(vs.hasPoolRef && vs.hasTcpProxyNetProfile),
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.udpEndToEndLabel),
                        configKey: 'e2e-timing-udp',
                        hidden: !(vs.hasPoolRef && vs.hasUDPNetProfile),
                    },
                ],
            },
            {
                icon: 'switch',
                headerText: this.l10nService.getMessage(l10nKeys.requestAnalyticsLabel),
                children: [
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.queryTypeLabel),
                        configKey: 'dns_qtype',
                    },
                    {
                        displayText: this.l10nService.getMessage(globalL10nKeys.requestLengthLabel),
                        configKey: 'rx_bytes',
                    },
                    {
                        displayText: this.l10nService.getMessage(
                            globalL10nKeys.responseLengthLabel,
                        ),
                        configKey: 'tx_bytes',
                    },
                    {
                        displayText: this.l10nService.getMessage(globalL10nKeys.protocolLabel),
                        configKey: 'protocol',
                    },
                ],
            },
            {
                icon: 'exclamation-circle',
                headerText: this.l10nService.getMessage(l10nKeys.significanceAnalyticsLabel),
                children: [
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.significanceLabel),
                        configKey: 'significance',
                    },
                ],
            },
        ];
    }

    /**
     * Set the config for the expander list view of non DNS VS based on headers and
     * children constants.
     */
    private getNonDnsExpanderListViewConfig(vs: IVsConfigForTrafficLogs): IRightRailExpanderConfig[] {
        return [
            {
                icon: 'shield',
                headerText: this.l10nService.getMessage(l10nKeys.wafAnalyticsLabel),
                hidden: !vs.hasWafPolicy,
                children: [
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.wafTagsLabel),
                        configKey: 'waf_log.rule_logs.tags',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.wafRulesLabel),
                        configKey: 'waf_log.rule_logs.rule_id',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.wafGroupsLabel),
                        configKey: 'waf-rule-groups',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.wafLatencyLabel),
                        configKey: 'waf-phase-latency',
                        hidden: !vs.isHTTP,
                    },
                ],
            },
            {
                icon: 'user',
                headerText: this.l10nService.getMessage(l10nKeys.clientAnalyticsLabel),
                children: [
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.browserLabel),
                        configKey: 'browser',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.clientOsLabel),
                        configKey: 'client_os',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.deviceLabel),
                        configKey: 'device',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(globalL10nKeys.ipAddressLabel),
                        configKey: 'ip-addr',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.locationLabel),
                        configKey: 'country',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.endToEndLabel),
                        configKey: 'e2e-timing',
                        hidden: vs.hasTCPFastPathNetProfile,
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.userIdLabel),
                        configKey: 'user_id',
                        hidden: !vs.isHTTP,
                    },
                ],
            },
            {
                icon: 'share',
                headerText: this.l10nService.getMessage(l10nKeys.loadBalancerAnalyticsLabel),
                children: [
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.policiesLabel),
                        configKey: 'policy',
                        hidden: !(vs.isHTTP && vs.policyEnabled),
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.vsIpAddressLabel),
                        configKey: 'vs_ip',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.serverIpAddressLabel),
                        configKey: 'server-ip-addr',
                    },
                    {
                        displayText: this.l10nService.getMessage(globalL10nKeys.serviceEngineLabel),
                        configKey: 'service_engine',
                    },
                ],
            },
            {
                icon: 'switch',
                hidden: !(vs.isHTTP || vs.appType === 'ssl'),
                headerText: this.l10nService.getMessage(l10nKeys.requestAnalyticsLabel),
                children: [
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.sslLabel),
                        configKey: 'ssl',
                        hidden: !vs.sslEnabled,
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.refererLabel),
                        configKey: 'referrer',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.urlPathLabel),
                        configKey: 'uri_path',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(globalL10nKeys.hostHeaderLabel),
                        configKey: 'host',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(globalL10nKeys.httpVersionLabel),
                        configKey: 'http_version',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(globalL10nKeys.requestLengthLabel),
                        configKey: 'request_length',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.requestTypeLabel),
                        configKey: 'request-type',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(globalL10nKeys.responseCodeLabel),
                        configKey: 'response-code',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.responseContentTypeLabel),
                        configKey: 'response_content_type',
                        hidden: !vs.isHTTP,
                    },
                    {
                        displayText: this.l10nService.getMessage(
                            globalL10nKeys.responseLengthLabel,
                        ),
                        configKey: 'response_length',
                        hidden: !vs.isHTTP,
                    },
                ],
            },
            {
                icon: 'bot',
                hidden: !vs.hasBotDetectionPolicy,
                headerText: this.l10nService.getMessage(l10nKeys.botAnalyticsLabel),
                children: [
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.classificationsLabel),
                        configKey: 'bot_management_log.classification.type',
                    },
                ],
            },
            {
                icon: 'shield',
                hidden: !vs.hasIcapProfile,
                headerText: this.l10nService.getMessage(l10nKeys.icapAnalyticsLabel),
                children: [
                    {
                        displayText: this.l10nService.getMessage(globalL10nKeys.actionLabel),
                        configKey: 'icap_log.action',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.latencyLabel),
                        configKey: 'icap_log.request_logs.latency',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.icapResponseCodeLabel),
                        configKey: 'icap_log.request_logs.icap_response_code',
                    },
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.httpResponseCodeLabel),
                        configKey: 'icap_log.request_logs.http_response_code',
                    },
                    {
                        displayText: this.l10nService.getMessage(globalL10nKeys.serverIpLabel),
                        configKey: 'icap_log.request_logs.icap_server_ip',
                    },
                    {
                        displayText: this.l10nService.getMessage(
                            l10nKeys.violationResolutionsLabel,
                        ),
                        configKey: 'icap_log.request_logs.opswat_log.violations.resolution',
                    },
                    {
                        displayText: this.l10nService.getMessage(
                            l10nKeys.violationThreatNamesLabel,
                        ),
                        configKey: 'icap_log.request_logs.opswat_log.violations.threat_name',
                    },
                ],
            },
            {
                icon: 'exclamation-circle',
                headerText: this.l10nService.getMessage(l10nKeys.significanceAnalyticsLabel),
                children: [
                    {
                        displayText: this.l10nService.getMessage(l10nKeys.significanceLabel),
                        configKey: 'significance',
                    },
                ],
            },
        ];
    }
}
